import { useState } from "react";
import { Address } from "ton";
import { Box, Fade, Link, Typography } from "@mui/material";
import { jettonDeployController, JettonDeployParams } from "lib/deploy-controller";
import WalletConnection from "services/wallet-connection";
import { createDeployParams } from "lib/utils";
import { ContractDeployer } from "lib/contract-deployer";
import { Link as ReactRouterLink } from "react-router-dom";
import { ROUTES } from "consts";
import useNotification from "hooks/useNotification";
import { FormWrapper, ScreenHeading, StyledDescription, SubHeadingWrapper } from "./styles";
import { Screen, ScreenContent } from "components/Screen";
import analytics, { AnalyticsAction, AnalyticsCategory } from "services/analytics";
import { getUrlParam, toDecimalsBN } from "utils";
import { offchainFormSpec, onchainFormSpec } from "./data";
import { Form } from "components/form";
import { GithubButton } from "pages/deployer/githubButton";
import { useNavigatePreserveQuery } from "lib/hooks/useNavigatePreserveQuery";
import { useTonAddress, useTonConnectUI } from "@tonconnect/ui-react";
import { TonSeoContent, TonSeoIntro } from "pages/home/TonSeo";
import { getTonConnectErrorMessage } from "lib/ton-connect-errors";

const DEFAULT_DECIMALS = 9;

const isOffchainInternal = getUrlParam("offchainINTERNAL") !== null;

let formSpec = isOffchainInternal ? offchainFormSpec : onchainFormSpec;

async function fetchDecimalsOffchain(url: string): Promise<{ decimals?: string }> {
  let res = await fetch(url);
  let obj = await res.json();
  return obj;
}

function DeployerPage() {
  const { showNotification } = useNotification();
  const walletAddress = useTonAddress();
  const [tonConnectUI] = useTonConnectUI();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigatePreserveQuery();

  async function deployContract(data: any) {
    if (!walletAddress || !tonConnectUI.connected || !tonConnectUI.account) {
      showNotification("请连接钱包", "warning");
      return;
    }

    setIsLoading(true);
    try {
      let decimals = data.decimals;
      if (data.offchainUri) {
        let res = await fetchDecimalsOffchain(
          data.offchainUri.replace("ipfs://", "https://ipfs.io/ipfs/"),
        );
        decimals = res.decimals;
      }

      const normalizedDecimals = decimals ?? DEFAULT_DECIMALS;
      if (!Number.isInteger(Number(normalizedDecimals)) || Number(normalizedDecimals) < 0) {
        throw new Error("代币精度无效");
      }

      const params: JettonDeployParams = {
        owner: Address.parse(walletAddress),
        onchainMetaData: {
          name: data.name,
          symbol: data.symbol,
          image: data.tokenImage,
          description: data.description,
          decimals: Number(normalizedDecimals).toFixed(0),
        },
        offchainUri: data.offchainUri,
        amountToMint: toDecimalsBN(data.mintAmount, normalizedDecimals),
      };
      const deployParams = createDeployParams(params, data.offchainUri);
      const contractAddress = new ContractDeployer().addressForContract(deployParams);

      const isDeployed = await WalletConnection.isContractDeployed(contractAddress);

      if (isDeployed) {
        showNotification(
          <>
            合约已部署,{" "}
            <ReactRouterLink to={`${ROUTES.jetton}/${Address.normalize(contractAddress)}/`}>
              查看合同
            </ReactRouterLink>
          </>,
          "warning",
        );
        setIsLoading(false);
        return;
      }

      const result = await jettonDeployController.createJetton(params, tonConnectUI);
      analytics.sendEvent(
        AnalyticsCategory.DEPLOYER_PAGE,
        AnalyticsAction.DEPLOY,
        contractAddress.toFriendly(),
      );

      navigate(`${ROUTES.jetton}/${Address.normalize(result)}`);
    } catch (error) {
      console.error("Failed to create jetton", error);
      showNotification(getTonConnectErrorMessage(error), "error");
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Screen>
      {/* <TonSeoIntro /> */}
      <ScreenContent removeBackground>
        <Fade in>
          <Box id="ton-token-form" sx={{ scrollMarginTop: 3 }}>
            <Box mb={3} mt={3.75}>
              <ScreenHeading variant="h5">铸造代币</ScreenHeading>
            </Box>
            <FormWrapper>
              <SubHeadingWrapper>
                <Form
                  isLoading={isLoading}
                  submitText="创建代币"
                  onSubmit={deployContract}
                  inputs={formSpec}
                />
                <div style={{ textAlign: "center" }}>费用：5 TON</div>
              </SubHeadingWrapper>
              <Box sx={{ flex: 4 }}>
                <Description />
              </Box>
            </FormWrapper>
          </Box>
        </Fade>
      </ScreenContent>
      {/* <TonSeoContent /> */}
    </Screen>
  );
}

export { DeployerPage };

const Spacer = () => {
  return <aside style={{ height: 25 }}></aside>;
};

function Description() {
  return (
    <StyledDescription sx={{ padding: 3 }}>
      <Typography
        variant="h5"
        mb={3}
        sx={{
          color: "#161C28",
          fontSize: 20,
          fontWeight: 800,
        }}>
        操作说明 |{" "}
        <Link href="https://help.bananatool.org/ton/createtoken" target="_blank">
          查看详细教程
        </Link>
      </Typography>
      <Typography
        sx={{
          fontWeight: 400,
          color: "#728A96",
          "& a": {
            textDecoration: "none",
            fontWeight: 500,
          },
        }}>
        <Link target="_blank" href="https://bananatool.org/">
          BananaTool
        </Link>
        提供的这个工具允许您一键将自己的代币部署到TON主网，您钱包内至少需要6个TON才能完成部署
        <br />
        <Spacer />
        代币发行之后，可以在本页面进行代币权限的管理，包括增发铸币、修改代币名称、更换logo等操作，我们建议您在代币创建成功后放弃权限
        <Spacer />
      </Typography>
      <GithubButton />
      <Spacer />
    </StyledDescription>
  );
}
